import { DISTRIBUTOR_FILTER_KEY_V2 } from 'elements/hardwareFilter/distributor/constants'
import type { FilterValuesType } from 'elements/hardwareFilter/typeV2'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'types/state'

/** Add filter keys here when product asks to persist more selector filters per project. */
const PERSISTABLE_FILTER_KEYS = [DISTRIBUTOR_FILTER_KEY_V2] as const

type ProjectKey = number | string
type Preferences = Partial<Pick<FilterValuesType, (typeof PERSISTABLE_FILTER_KEYS)[number]>>

const preferencesByProject = new Map<string, Preferences>()

const resolveProjectId = (projectId: ProjectKey | null | undefined): ProjectKey | null | undefined => {
  if (projectId != null) return projectId
  if (window.WorkspaceHelper?.project?.id != null) return window.WorkspaceHelper.project.id
  if (window.projectForm?.getState()?.values?.id != null) return window.projectForm.getState().values.id
  return null
}

const readPreferences = (projectId: ProjectKey | null | undefined): Preferences => {
  const resolved = resolveProjectId(projectId)
  if (resolved == null) return {}
  return preferencesByProject.get(String(resolved)) ?? {}
}

const writePreference = (
  projectId: ProjectKey | null | undefined,
  key: (typeof PERSISTABLE_FILTER_KEYS)[number],
  value: unknown
) => {
  const resolved = resolveProjectId(projectId)
  if (resolved == null) return

  const storeKey = String(resolved)
  const preferences = readPreferences(resolved)
  const currentValue = preferences[key]

  if (value == null) {
    if (currentValue === undefined) return
    const next = { ...preferences }
    delete next[key]
    if (Object.keys(next).length === 0) {
      preferencesByProject.delete(storeKey)
    } else {
      preferencesByProject.set(storeKey, next)
    }
    return
  }

  if (currentValue === value) return
  preferencesByProject.set(storeKey, { ...preferences, [key]: value })
}

const toDefaultFilterValues = (preferences: Preferences): Partial<FilterValuesType> => {
  const defaults: Partial<FilterValuesType> = {}
  for (const key of PERSISTABLE_FILTER_KEYS) {
    if (key in preferences) {
      defaults[key] = preferences[key]
    }
  }
  return defaults
}

type SetBlockRequest = (block: boolean) => void

/** Seed values for a newly opened selector dialog (captured once per mount). */
export const useProjectHardwareSelectorPersistedFilterDefaults = (enabled = true): Partial<FilterValuesType> => {
  const projectIdFromRedux = useSelector((state: RootState) => state.projectId)
  const [defaults] = useState(() => (enabled ? toDefaultFilterValues(readPreferences(projectIdFromRedux)) : {}))
  return defaults
}

/** Persist allowlisted filter keys across selector opens within the same project session. */
export const useProjectHardwareSelectorFilterPersistence = ({
  enabled = true,
  modifiableFilterValues,
  setBlockRequest,
}: {
  enabled?: boolean
  modifiableFilterValues: FilterValuesType | undefined
  setBlockRequest: SetBlockRequest
}) => {
  const projectIdFromRedux = useSelector((state: RootState) => state.projectId)
  const projectId = resolveProjectId(projectIdFromRedux)
  const filtersReadyRef = useRef(false)
  const hadActiveDistributorsRef = useRef(false)
  const modifiableFilterValuesRef = useRef(modifiableFilterValues)
  modifiableFilterValuesRef.current = modifiableFilterValues

  const syncPreferences = useCallback(
    (filterValues: FilterValuesType | undefined) => {
      if (!enabled || !filterValues) return

      const hasDistributors = DISTRIBUTOR_FILTER_KEY_V2 in filterValues
      const distributorsValue = filterValues[DISTRIBUTOR_FILTER_KEY_V2]

      if (hasDistributors && distributorsValue != null) {
        hadActiveDistributorsRef.current = true
        writePreference(projectId, DISTRIBUTOR_FILTER_KEY_V2, distributorsValue)
        return
      }

      if (filtersReadyRef.current && hadActiveDistributorsRef.current && !hasDistributors) {
        hadActiveDistributorsRef.current = false
        writePreference(projectId, DISTRIBUTOR_FILTER_KEY_V2, null)
      }
    },
    [enabled, projectId]
  )

  const handleFiltersReady = useCallback(() => {
    filtersReadyRef.current = true
    setBlockRequest(false)
    syncPreferences(modifiableFilterValuesRef.current)
  }, [setBlockRequest, syncPreferences])

  useEffect(() => {
    syncPreferences(modifiableFilterValues)
  }, [modifiableFilterValues, syncPreferences])

  useEffect(() => {
    return () => syncPreferences(modifiableFilterValuesRef.current)
  }, [syncPreferences])

  return {
    handleFiltersReady: enabled ? handleFiltersReady : () => setBlockRequest(false),
  }
}
